import type { inferRouterOutputs } from '@trpc/server';
import { Card, StatusBadge } from '@/components/UI';
import type { AppRouter } from '@/types/appRouter';
import { formatTimestamp } from '../entryOptimizerUtils';

export type EntryOptimizerConfirmation = inferRouterOutputs<AppRouter>['entryOptimizer']['listConfirmations']['confirmations'][number];

/**
 * Confirmation runs re-discover entries with a candidate's parameters applied to
 * the pristine source logic; entry-count parity with the analytic prediction is
 * the standing correctness check on the sweep machinery.
 */
const ConfirmationsCard = ({ confirmations }: { confirmations: EntryOptimizerConfirmation[] }) => {
    if (confirmations.length === 0) return null;

    return (
        <Card
            className="mt-4"
            subtitle="A verification re-tests a setting with a real run and checks it matches the prediction — required before making a setting live."
            title="Verification Runs"
        >
            <div className="flex flex-col gap-2">
                {confirmations.map((confirmation) => (
                    <div
                        className="flex flex-col gap-1 rounded-md border border-zinc-800 bg-black/20 p-3 md:flex-row md:items-center md:justify-between"
                        key={confirmation.id}
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-zinc-100">Verifying a suggested setting</div>
                            <div className="mt-0.5 text-xs text-zinc-500">Started {formatTimestamp(confirmation.createdAt)}</div>
                            {confirmation.errorMessage && <div className="mt-1 text-xs text-red-300">{confirmation.errorMessage}</div>}
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-3">
                            <span className="text-xs text-zinc-400">
                                {confirmation.discoveredEntryCount?.toLocaleString() ?? '…'} entries (predicted{' '}
                                {confirmation.predictedEntryCount?.toLocaleString() ?? '—'})
                            </span>
                            <StatusBadge status={confirmation.status}>{confirmation.status}</StatusBadge>
                            {confirmation.status === 'completed' && (
                                <StatusBadge
                                    status={confirmation.parityVerdict}
                                    tone={confirmation.parityVerdict === 'passed' ? 'success' : 'danger'}
                                >
                                    {confirmation.parityVerdict === 'passed' ? 'verified' : 'mismatch'}
                                </StatusBadge>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export default ConfirmationsCard;
